import "shared/extensions/date";

import { useCallback, useEffect, useMemo, useState } from "react";
import { EventNames } from "core/monitoring/types/enums";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import { RecoveryCodesModal } from "./Components/RecoveryCodesModal/RecoveryCodesModal";
import { Setup } from "./Components/Setup/Setup";
import Button from "shared/UI/Buttons/Button";
import { Verify } from "./Components/Verify/Verify";
import classNames from "classnames";
import { trackBtnClicks } from "core/monitoring/Events";
import { useModalContext } from "shared/core/state/ModalContext/ModalContext";
import { useTranslation } from "react-i18next";
import { NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import { useNavBarContext } from "core/state/NavBarContext/NavBarContext";
import { useNavigate } from "react-router-dom";

export const TwoFactorAuthentication = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [otp, setOtp] = useState("");
  const navigate = useNavigate();
  const { isVisible, hide, show } = useModalContext("verify_twofa_modal");

  const steps = useMemo(() => {
    return [
      {
        id: EventNames.SETUP_TWO_FA,
        Component: <Setup />,
      },
      {
        id: EventNames.VERIFY_TWO_FA,
        Component: <Verify otp={otp} handleChangeOtp={setOtp} />,
      },
    ];
  }, [otp]);

  const handleStepRender = steps[step].Component;

  const handleClick = useCallback(
    (action: "Next" | "Prev") => {
      const Next = step + 1;
      const Prev = step - 1;
      if (action === "Next") {
        if (step !== steps.length - 1) {
          setStep(Next);
          trackBtnClicks(steps[Next].id);
        } else {
          show();
        }
      } else if (action === "Prev") {
        setStep(Prev);
        setOtp("");
        trackBtnClicks(steps[Prev].id);
      }
    },
    [show, step, steps]
  );

  const handleBack = useCallback(() => (step !== 0 ? () => handleClick("Prev") : () => navigate(-1)), [handleClick, step, navigate]);

  useNavBarContext({ callback: handleBack, title: t("pageTitleOverride.2fa") });

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        handleClick("Next");
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleClick]);

  return (
    <>
      <PageTitle title={t("pageTitle.account.accountSecurity.twoFactorAuth")} />
      <PageHeader title={t("accountSecurity.twoFactorAuth.heading")} hideForMobile narrowView onClickBack={handleBack()} />
      <NarrowContainer>
        {handleStepRender}
        <div
          className={classNames("flex flex-col items-center mt-16", {
            "justify-end": step === 0,
            "justify-between gap-4 lg:min-w-[500px]": step !== 0,
          })}
        >
          <Button
            type="submit"
            text={t(step === steps.length - 1 ? "navigation.submitButton" : "navigation.nextButton")}
            onClick={() => handleClick("Next")}
          />
        </div>
        <div className={classNames({ "min-h-[500px]": step !== 0 })} />
      </NarrowContainer>
      <RecoveryCodesModal isVisible={isVisible} onDismiss={hide} status="new" code={otp} />
    </>
  );
};
